import React, { useState } from "react";
import { X, Upload, CheckSquare, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { addMinutes, computeDuration, uid } from "@/lib/utils";

const FIELDS = [
  { key: "date", label: "Date", required: false, hint: "Optionnel; sinon date de l'événement" },
  { key: "start_time", label: "Heure début", required: true, hint: "Ex: 8:00, 17h20" },
  { key: "end_time", label: "Heure fin", required: false, hint: "Si absent, calculé via durée" },
  { key: "duration", label: "Durée (min)", required: false, hint: "Sinon durée par défaut" },
  { key: "surface", label: "Surface / Emplacement", required: true, hint: "Numéro ou nom" },
  { key: "category", label: "Catégorie", required: false, hint: "Ex: U13 A" },
  { key: "team_a", label: "Équipe locale (A)", required: false },
  { key: "team_b", label: "Équipe visiteur (B)", required: false },
  { key: "game_number", label: "No. de partie", required: false },
];

function autoDetect(headers) {
  const find = (patterns) => {
    for (const h of headers) {
      const low = String(h).toLowerCase();
      if (patterns.some((p) => low.includes(p))) return h;
    }
    return "";
  };
  return {
    date: find(["date"]),
    start_time: find(["heure début", "heure de début", "début", "start", "heure", "time"]),
    end_time: find(["heure fin", "heure de fin", "fin", "end"]),
    duration: find(["durée", "duree", "duration", "temps"]),
    surface: find(["emplacement", "surface", "terrain", "aréna", "arena", "glace", "patinoire", "court", "rink", "lieu"]),
    category: find(["catégorie", "categorie", "category", "classe", "division", "niveau"]),
    team_a: find(["équipe locale", "equipe locale", "locale", "home", "domicile", "équipe a", "equipe a", "team a"]),
    team_b: find(["équipe visiteur", "equipe visiteur", "visiteur", "away", "équipe b", "equipe b", "team b"]),
    game_number: find(["no. de partie", "no partie", "numéro", "numero", "#"]),
  };
}

function parseTime(input) {
  if (input == null) return null;
  const s = String(input).trim();
  if (!s) return null;
  if (s.includes("T")) {
    const d = new Date(s);
    if (!isNaN(d)) return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  if (/^0?\.\d+$/.test(s) || /^\d+\.\d+$/.test(s)) {
    const n = parseFloat(s);
    if (n >= 0 && n < 1) {
      const total = Math.round(n * 24 * 60);
      return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
    }
  }
  const cleaned = s.toLowerCase().replace(/h/g, ":").replace(/\./g, ":");
  const m = cleaned.match(/^(\d{1,2})[:\s]?(\d{0,2})\s*(am|pm)?$/i);
  if (m) {
    let h = parseInt(m[1]);
    let mn = parseInt(m[2] || "0");
    if (m[3]) {
      if (m[3].toLowerCase() === "pm" && h < 12) h += 12;
      if (m[3].toLowerCase() === "am" && h === 12) h = 0;
    }
    if (h >= 0 && h <= 23 && mn >= 0 && mn <= 59) return `${String(h).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
  }
  return null;
}

function parseDate(input) {
  if (!input) return "";
  const s = String(input).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!isNaN(d)) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) {
    let [, d1, d2, y] = m;
    if (y.length === 2) y = "20" + y;
    return `${y}-${d2.padStart(2, "0")}-${d1.padStart(2, "0")}`;
  }
  return "";
}

export default function ImportExcelModal({ event, onApply, onClose }) {
  const [step, setStep] = useState("upload"); // upload | mapping | duration | preview | confirm
  const [rows, setRows] = useState([]);
  const [headerRow, setHeaderRow] = useState(0);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const hasExistingGames = (event.games || []).some((g) => !g.is_break);
  const [mode, setMode] = useState(hasExistingGames ? "sync" : "replace");
  const [loading, setLoading] = useState(false);
  const [defaultDuration, setDefaultDuration] = useState(event.game_duration || 45);

  const loadFile = async (file) => {
    setLoading(true);
    try {
      if (!window.XLSX) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(buf, { type: "array", cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = window.XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
      const cleaned = data.filter((r) => r.some((c) => String(c).trim() !== ""));
      setRows(cleaned);
      // Auto-detect header row
      let detected = 0;
      for (let i = 0; i < Math.min(5, cleaned.length); i++) {
        const row = cleaned[i];
        const nonEmpty = row.filter((c) => String(c).trim() !== "");
        if (nonEmpty.length >= 3 && nonEmpty.some((c) => /heure|surface|équipe|catégorie|début|temps/i.test(String(c)))) {
          detected = i;
          break;
        }
      }
      setHeaderRow(detected);
      const hdrs = (cleaned[detected] || []).map((h) => String(h).trim());
      setHeaders(hdrs);
      setMapping(autoDetect(hdrs));
      setStep("mapping");
    } catch (err) {
      toast.error("Erreur de lecture : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPreview = () => {
    if (!mapping.start_time || !mapping.surface) {
      toast.error("Heure début et Surface obligatoires");
      return;
    }
    const dataRows = rows.slice(headerRow + 1);
    const games = [];
    const errs = [];
    dataRows.forEach((row, idx) => {
      const get = (key) => {
        const colIdx = headers.indexOf(mapping[key]);
        return colIdx >= 0 ? String(row[colIdx] || "").trim() : "";
      };
      const startRaw = get("start_time");
      if (!startRaw) return;
      const start = parseTime(startRaw);
      if (!start) {
        errs.push(`Ligne ${headerRow + idx + 2} : heure invalide "${startRaw}"`);
        return;
      }
      let end = "";
      const endRaw = get("end_time");
      const durRaw = get("duration");
      let duration = parseInt(durRaw) || 0;
      if (endRaw) {
        end = parseTime(endRaw);
        if (end && !duration) duration = computeDuration(start, end);
      }
      if (!end && duration) end = addMinutes(start, duration);
      if (!end) {
        duration = defaultDuration || event.game_duration || 45;
        end = addMinutes(start, duration);
      }
      const surface = get("surface") || "1";
      const dateStr = parseDate(get("date"));
      games.push({
        id: uid(),
        surface,
        date: dateStr || event.date || "",
        start_time: start,
        end_time: end,
        duration,
        category: get("category"),
        team_a: get("team_a"),
        team_b: get("team_b"),
        game_number: get("game_number"),
        referee1_id: null,
        referee2_id: null,
        scorer_id: null,
        is_break: false,
        break_label: "",
      });
    });
    games.sort((a, b) => (a.date || "").localeCompare(b.date || "") || a.start_time.localeCompare(b.start_time));
    setParsed(games);
    setErrors(errs);
    setStep("preview");
  };

  const continueFromMapping = () => {
    if (!mapping.start_time || !mapping.surface) {
      toast.error("Heure début et Surface obligatoires");
      return;
    }
    // If neither end_time nor duration column is mapped, ask user for game duration
    if (!mapping.end_time && !mapping.duration) {
      setStep("duration");
      return;
    }
    buildPreview();
  };

  const commit = async (overrideMode = null) => {
    // Fallback robuste : si aucun mode n'est défini, on choisit la valeur la plus sûre
    // selon le contexte (sync si l'événement contient déjà des parties, sinon replace).
    const hasGamesNow = (event.games || []).some((g) => !g.is_break);
    const effMode = overrideMode || mode || (hasGamesNow ? "sync" : "replace");
    let nextGames;
    if (effMode === "replace") {
      nextGames = parsed;
    } else if (effMode === "append") {
      nextGames = [...(event.games || []), ...parsed];
    } else if (effMode === "sync") {
      // Synchroniser : remplace les parties mais préserve les assignations
      // pour les parties qui matchent par (date + heure + surface).
      const oldByKey = {};
      (event.games || []).forEach((g) => {
        if (g.is_break) return;
        const key = `${g.date || ""}|${g.start_time || ""}|${String(g.surface || "")}`;
        oldByKey[key] = g;
      });
      // Conserver toutes les pauses
      const breaks = (event.games || []).filter((g) => g.is_break);
      // Pour chaque nouvelle partie, hériter des assignations de l'ancienne au même slot
      nextGames = [
        ...breaks,
        ...parsed.map((g) => {
          const key = `${g.date || ""}|${g.start_time || ""}|${String(g.surface || "")}`;
          const old = oldByKey[key];
          if (old) {
            return {
              ...g,
              referee1_id: old.referee1_id || null,
              referee2_id: old.referee2_id || null,
              scorer_id: old.scorer_id || null,
            };
          }
          return g;
        }),
      ];
    } else {
      toast.error("Mode d'importation manquant — choisis Remplacer, Ajouter ou Synchroniser.");
      return;
    }
    await onApply(nextGames, effMode);
    onClose();
  };

  // Compteurs pour le mode "Sync" : nombre de parties qui matchent un ancien slot
  const syncStats = (() => {
    if (mode !== "sync") return null;
    const oldByKey = {};
    let oldWithAssignments = 0;
    (event.games || []).forEach((g) => {
      if (g.is_break) return;
      const hasAssign = !!(g.referee1_id || g.referee2_id || g.scorer_id);
      if (hasAssign) oldWithAssignments++;
      const key = `${g.date || ""}|${g.start_time || ""}|${String(g.surface || "")}`;
      oldByKey[key] = g;
    });
    let matched = 0;
    let preservedAssignments = 0;
    parsed.forEach((g) => {
      const key = `${g.date || ""}|${g.start_time || ""}|${String(g.surface || "")}`;
      const old = oldByKey[key];
      if (old) {
        matched++;
        if (old.referee1_id || old.referee2_id || old.scorer_id) preservedAssignments++;
      }
    });
    return {
      matched,
      lost: Math.max(0, oldWithAssignments - preservedAssignments),
      added: parsed.length - matched,
      preservedAssignments,
    };
  })();

  const hasExisting = (event.games || []).some((g) => !g.is_break);
  const uniqueDates = [...new Set(parsed.map((g) => g.date).filter(Boolean))];

  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-3xl w-full shadow-2xl my-8" data-testid="import-excel-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label">Importation Excel</div>
            <h3 className="font-serif text-3xl">
              {step === "upload" && "Choisis ton fichier"}
              {step === "mapping" && "Associer les colonnes"}
              {step === "duration" && "Durée des parties"}
              {step === "preview" && "Aperçu"}
              {step === "confirm" && "Mode d'importation"}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-black"><X size={20} /></button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-6">
          {(() => {
            const allSteps = ["upload", "mapping", ...(step === "duration" ? ["duration"] : []), "preview", "confirm"];
            const idx = allSteps.indexOf(step);
            return allSteps.map((s, i) => {
              const active = i === idx;
              const done = i < idx;
              return (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 border-2 border-black flex items-center justify-center font-mono text-xs font-bold ${active ? "bg-black text-white" : done ? "bg-black text-white" : "bg-white"}`}>
                    {done ? <CheckSquare size={14} strokeWidth={3} /> : i + 1}
                  </div>
                  {i < allSteps.length - 1 && <div className="flex-1 h-[2px] bg-black/30" />}
                </React.Fragment>
              );
            });
          })()}
        </div>

        {step === "upload" && (
          <div>
            <p className="text-sm text-[var(--muted)] mb-5 leading-relaxed">
              Sélectionne un fichier Excel (.xlsx, .xls) ou CSV. Tu pourras associer les colonnes à l'étape suivante.
            </p>
            {loading ? (
              <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse text-center py-12">Lecture…</p>
            ) : (
              <label className="block border-2 border-dashed border-black p-12 text-center hover:bg-[var(--surface-3)] cursor-pointer transition-colors" data-testid="excel-dropzone">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => e.target.files?.[0] && loadFile(e.target.files[0])}
                  className="hidden"
                />
                <Upload size={32} strokeWidth={2} className="mx-auto mb-3" />
                <p className="font-bold text-sm mb-1">Cliquer pour choisir un fichier</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">.xlsx, .xls ou .csv</p>
              </label>
            )}
          </div>
        )}

        {step === "mapping" && (
          <div>
            <p className="text-sm text-[var(--muted)] mb-4 leading-relaxed">
              Indique quelle colonne du fichier correspond à chaque champ. L'app a tenté une détection automatique.
            </p>
            <div className="mb-4">
              <label className="label block mb-1.5">Ligne d'en-tête</label>
              <select
                value={headerRow}
                onChange={(e) => {
                  const r = parseInt(e.target.value);
                  setHeaderRow(r);
                  const hdrs = (rows[r] || []).map((h) => String(h).trim());
                  setHeaders(hdrs);
                  setMapping(autoDetect(hdrs));
                }}
                className="input-box"
              >
                {rows.slice(0, 8).map((row, i) => (
                  <option key={i} value={i}>
                    Ligne {i + 1} : {row.slice(0, 5).map((c) => String(c).slice(0, 15)).join(" | ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="label block mb-1.5">
                    {field.label} {field.required && <span className="text-black font-bold">*</span>}
                  </label>
                  {field.hint && <p className="font-mono text-[10px] text-[var(--muted)] mb-1">{field.hint}</p>}
                  <select
                    value={mapping[field.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [field.key]: e.target.value })}
                    className="input-box"
                    data-testid={`map-${field.key}`}
                  >
                    <option value="">— Aucune colonne —</option>
                    {headers.map((h, i) => h && <option key={i} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === "duration" && (
          <div>
            <div className="border-2 border-black bg-conflict-stripes-light p-3 mb-5 text-xs">
              ℹ Ton fichier <b>ne contient pas d'heure de fin</b> (ni de colonne « Durée »). Indique combien de temps dure chaque partie pour calculer l'heure de fin automatiquement.
            </div>
            <label className="label block mb-1.5">Durée par partie (minutes) <span className="text-black font-bold">*</span></label>
            <p className="font-mono text-[10px] text-[var(--muted)] mb-2">Cette durée sera appliquée à toutes les parties importées.</p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                max={240}
                step={1}
                value={defaultDuration}
                onChange={(e) => setDefaultDuration(parseInt(e.target.value) || 0)}
                className="input-box max-w-[140px] text-center tabular text-lg font-bold"
                data-testid="import-default-duration-input"
                autoFocus
              />
              <span className="font-mono text-xs text-[var(--muted)]">minutes</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {[30, 45, 50, 60, 75, 90].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDefaultDuration(m)}
                  className={`px-3 py-1.5 border-2 font-mono text-xs ${defaultDuration === m ? "border-black bg-black text-white" : "border-[var(--border-strong)] hover:border-black"}`}
                  data-testid={`duration-preset-${m}`}
                >
                  {m} min
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "preview" && (
          <div>
            {uniqueDates.length > 1 && (
              <div className="border-2 border-black bg-conflict-stripes-light p-3 mb-4 text-xs">
                ℹ Le fichier contient <b>{uniqueDates.length} dates différentes</b> ({uniqueDates.slice(0, 3).join(", ")}). Chaque partie conservera sa date.
              </div>
            )}
            {errors.length > 0 && (
              <div className="border-2 border-black bg-conflict-stripes-light p-3 mb-4 text-xs">
                <div className="font-bold uppercase tracking-wider mb-1">⚠ {errors.length} avertissement(s)</div>
                <ul className="space-y-0.5">
                  {errors.slice(0, 5).map((e, i) => <li key={i}>· {e}</li>)}
                  {errors.length > 5 && <li>· (et {errors.length - 5} autres)</li>}
                </ul>
              </div>
            )}
            <div className="font-mono text-xs text-[var(--muted)] mb-3">
              <span className="text-black font-bold tabular">{parsed.length}</span> partie{parsed.length > 1 ? "s" : ""} détectée{parsed.length > 1 ? "s" : ""}
            </div>
            <div className="border-t-2 border-b-2 border-black max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#F5F5F0]">
                  <tr className="border-b-2 border-black">
                    {parsed.some((p) => p.date) && <th className="py-2 px-2 text-left label">Date</th>}
                    <th className="py-2 px-2 text-left label">Heure</th>
                    <th className="py-2 px-2 text-left label">Surface</th>
                    <th className="py-2 px-2 text-left label">Catégorie</th>
                    <th className="py-2 px-2 text-left label">Équipes</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((g) => (
                    <tr key={g.id} className="border-b border-[var(--border)]">
                      {parsed.some((p) => p.date) && <td className="py-1.5 px-2">{g.date || "—"}</td>}
                      <td className="py-1.5 px-2 tabular">{g.start_time}–{g.end_time}</td>
                      <td className="py-1.5 px-2">{g.surface}</td>
                      <td className="py-1.5 px-2">{g.category || "—"}</td>
                      <td className="py-1.5 px-2">{g.team_a || "—"} vs {g.team_b || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-3">
            <p className="text-sm text-[var(--muted)] leading-relaxed">
              L'événement contient déjà {(event.games || []).filter((g) => !g.is_break).length} partie(s). Que veux-tu faire?
            </p>
            <button
              onClick={() => setMode("sync")}
              className={`w-full text-left p-4 border-2 rounded-xl transition-colors ${mode === "sync" ? "border-black bg-black/5" : "border-[var(--border-strong)] hover:border-black"}`}
              data-testid="mode-sync-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                {mode === "sync" && <CheckSquare size={14} strokeWidth={3} />}
                <span className="font-bold text-sm">⭐ Synchroniser (conserver les assignations)</span>
              </div>
              <p className="text-[11px] uppercase tracking-widest text-[var(--muted)] mb-2">
                RECOMMANDÉ POUR LES RÉ-IMPORTATIONS APRÈS MODIFICATIONS
              </p>
              <p className="text-xs text-[var(--text-2)] leading-relaxed">
                Remplace l'horaire mais réutilise les assignations d'arbitres/marqueurs pour les parties qui ont la <strong>même date + heure + surface</strong>. Les équipes peuvent changer sans impact. Les pauses sont conservées.
              </p>
              {mode === "sync" && syncStats && (
                <div className="mt-3 pt-3 border-t border-[var(--border)] grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[var(--muted)]">Parties qui matchent : </span>
                    <span className="font-bold tabular">{syncStats.matched}</span>
                  </div>
                  <div>
                    <span className="text-[var(--muted)]">Nouvelles parties : </span>
                    <span className="font-bold tabular">{syncStats.added}</span>
                  </div>
                  <div>
                    <span className="text-[var(--muted)]">Assignations conservées : </span>
                    <span className="font-bold tabular">{syncStats.preservedAssignments}</span>
                  </div>
                  <div>
                    <span className="text-[var(--muted)]">Perdues (slot disparu) : </span>
                    <span className={`font-bold tabular ${syncStats.lost > 0 ? "text-[var(--danger)]" : ""}`}>{syncStats.lost}</span>
                  </div>
                </div>
              )}
            </button>
            <button
              onClick={() => setMode("append")}
              className={`w-full text-left p-4 border-2 rounded-xl transition-colors ${mode === "append" ? "border-black bg-black/5" : "border-[var(--border-strong)] hover:border-black"}`}
              data-testid="mode-append-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                {mode === "append" && <CheckSquare size={14} strokeWidth={3} />}
                <span className="font-bold text-sm">Ajouter aux parties existantes</span>
              </div>
              <p className="text-[11px] uppercase tracking-widest text-[var(--muted)]">
                Les {parsed.length} nouvelles parties s'ajoutent à l'horaire. Total : {(event.games || []).length + parsed.length}.
              </p>
            </button>
            <button
              onClick={() => setMode("replace")}
              className={`w-full text-left p-4 border-2 rounded-xl transition-colors ${mode === "replace" ? "border-black bg-conflict-stripes-light" : "border-[var(--border-strong)] hover:border-black"}`}
              data-testid="mode-replace-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                {mode === "replace" && <CheckSquare size={14} strokeWidth={3} />}
                <span className="font-bold text-sm">⚠ Remplacer toutes les parties</span>
              </div>
              <p className="text-[11px] uppercase tracking-widest text-[var(--muted)]">
                Les {(event.games || []).length} entrées actuelles seront supprimées (pauses + assignations incluses).
              </p>
            </button>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-[var(--border)] gap-2">
          <button onClick={onClose} className="btn-ghost">Annuler</button>
          <div className="flex gap-2">
            {step === "mapping" && (
              <>
                <button onClick={() => setStep("upload")} className="btn-secondary"><ArrowLeft size={14} strokeWidth={2.5} /> Retour</button>
                <button onClick={continueFromMapping} className="btn-primary" data-testid="map-continue-btn">Continuer</button>
              </>
            )}
            {step === "duration" && (
              <>
                <button onClick={() => setStep("mapping")} className="btn-secondary"><ArrowLeft size={14} strokeWidth={2.5} /> Retour</button>
                <button
                  onClick={() => {
                    if (!defaultDuration || defaultDuration < 1) {
                      toast.error("Indique une durée valide (en minutes)");
                      return;
                    }
                    buildPreview();
                  }}
                  className="btn-primary"
                  data-testid="duration-continue-btn"
                >
                  Continuer
                </button>
              </>
            )}
            {step === "preview" && (
              <>
                <button onClick={() => setStep((!mapping.end_time && !mapping.duration) ? "duration" : "mapping")} className="btn-secondary"><ArrowLeft size={14} strokeWidth={2.5} /> Retour</button>
                {parsed.length > 0 && (
                  hasExisting ? (
                    <button onClick={() => setStep("confirm")} className="btn-primary" data-testid="preview-continue-btn">Continuer</button>
                  ) : (
                    <button onClick={() => { setMode("replace"); commit("replace"); }} className="btn-primary" data-testid="import-now-btn">
                      Importer {parsed.length} partie{parsed.length > 1 ? "s" : ""}
                    </button>
                  )
                )}
              </>
            )}
            {step === "confirm" && (
              <>
                <button onClick={() => setStep("preview")} className="btn-secondary"><ArrowLeft size={14} strokeWidth={2.5} /> Retour</button>
                <button onClick={() => commit()} className="btn-primary" data-testid="confirm-import-btn">
                  {mode === "replace" ? "Remplacer" : mode === "sync" ? "Synchroniser" : mode === "append" ? "Ajouter" : "Importer"} {parsed.length} partie{parsed.length > 1 ? "s" : ""}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
